import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CommonSymbolListsService,
  SymbolListSummary,
} from '../../../@services/common-symbol-lists.service';
import { SymbolListsRegistryService } from '../../../@services/symbol-lists-registry.service';
import { ToastService } from '../../../@services/toast.service';
import { CopySourceButtonComponent } from '../../../@shared/copy-source-button/copy-source-button.component';

// Source repo for New-list registry entries. Legacy default; the
// Registry panel is where operators repoint an existing list at a
// different repo. Any change here should stay in sync with the
// backend's legacy-mode default in symbol-lists.service.ts (which uses
// the SYMBOL_LISTS_REPO env, resolving to the same slug).
const DEFAULT_REGISTRY_REPO = 'F2Tech-WebDev-v2/common-symbol-lists';
const DEFAULT_REGISTRY_BRANCH = 'main';

// GitHub Symbol Lists pane — first source under /admin/common-data.
// Two columns: left list picker, right editor. Textarea holds the raw
// file bytes so the operator sees comments + blank lines, not just the
// parsed SET. Save PUTs to the API which commits to GitHub and kicks a
// Redis refresh in the same call.
//
// Extracted from the pre-graduation /admin/common-symbol-lists panel
// on 2026-07-16. The outer panel header + description now live in
// common-data.component.html; this component owns only the two-column
// editor UI so it composes cleanly into the shell.
@Component({
  selector: 'app-github-symbol-lists',
  standalone: true,
  imports: [CommonModule, FormsModule, CopySourceButtonComponent],
  templateUrl: './github-symbol-lists.component.html',
})
export class GithubSymbolListsComponent implements OnInit {
  // Emits when the operator clicks the settings gear on a list row.
  // The hub (CommonDataComponent) listens and swaps to the Registry
  // panel with the name preselected — so "edit this list's definition"
  // is one click away from the file editor.
  @Output() editDefinition = new EventEmitter<string>();

  lists: SymbolListSummary[] = [];
  loading = false;

  mode: 'idle' | 'view' | 'new' = 'idle';
  selectedName = '';
  newName = '';
  editorContent = '';
  editorSha = '';
  editorDirty = false;
  saving = false;
  deleting = false;

  // IT-F2-99: new-list flow now supports txt AND csv, with a per-format
  // ticker column selector for csv. The type toggle is new-list-only —
  // an existing list's format is fixed by its registry doc + repo path.
  // Toggling type post-facto would require a PATCH-registry + move-file
  // dance that's out of scope for this ticket.
  newFormat: 'txt' | 'csv' = 'txt';
  newTickerColumn = '';
  // IT-F2-100: new-list flow now lets the operator pick the target
  // repo — e.g. customer-scoped repos like `F2Tech-WebDev-v2/theo-
  // trade-symbol-lists` — instead of always committing to the shared
  // common-symbol-lists repo. Existing lists' repo is repointed via
  // the Registry panel (unchanged).
  //
  // Split into a dropdown-selected value + a text-input override for
  // the `Other…` case (first-time commit to a brand-new repo). newRepo
  // is the effective value that reaches registry.create — either the
  // dropdown pick OR the free-text field when 'other' is active.
  newRepo = DEFAULT_REGISTRY_REPO;
  newRepoOther = '';
  // Sentinel value stored on the <select> when the operator picks the
  // "Other…" entry, so we can distinguish it from a real repo slug.
  readonly REPO_OTHER = '__other__';
  // Track whether the operator has manually picked a ticker column so
  // the auto-pick heuristic doesn't overwrite their choice on every
  // keystroke that reparses the header row.
  private _tickerColumnManuallyPicked = false;
  // View-mode format — derived from the selected list's manifest so the
  // `foo.txt` / `foo.csv` filename badge in the editor header is right.
  viewFormat: 'txt' | 'csv' = 'txt';

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(
    private svc: CommonSymbolListsService,
    private registry: SymbolListsRegistryService,
    private toast: ToastService,
  ) {}

  openDefinition(name: string, evt: Event) {
    // Stop propagation so the row-body click handler (selectList) doesn't
    // also fire and load the file editor in the same gesture.
    evt.stopPropagation();
    if (this.editorDirty && !confirm(`Discard unsaved changes to "${this.selectedName || this.newName}" first?`)) return;
    this.editDefinition.emit(name);
  }

  async ngOnInit() {
    await this.reload();
  }

  async reload() {
    this.loading = true;
    try {
      this.lists = await this.svc.listAll();
    } finally {
      this.loading = false;
    }
  }

  async selectList(name: string) {
    if (this.editorDirty && !confirm(`Discard unsaved changes to "${this.selectedName || this.newName}"?`)) return;
    const f = await this.svc.getFile(name);
    if (!f) {
      this.toast.error('Load failed', `Could not load ${name} — it may have been deleted.`);
      await this.reload();
      return;
    }
    this.mode = 'view';
    this.selectedName = f.name;
    this.newName = '';
    this.editorContent = f.content;
    this.editorSha = f.sha;
    this.editorDirty = false;
    // Pick the on-disk extension from the summary manifest so the
    // filename badge is honest (e.g. `sp500.csv` for a csv-format list).
    const summary = this.lists.find(l => l.name === f.name);
    this.viewFormat = summary?.manifest?.format === 'csv' ? 'csv' : 'txt';
  }

  startNew() {
    if (this.editorDirty && !confirm('Discard unsaved changes?')) return;
    this.mode = 'new';
    this.selectedName = '';
    this.newName = '';
    this.editorContent = '';
    this.editorSha = '';
    this.editorDirty = false;
    this.newFormat = 'txt';
    this.newTickerColumn = '';
    this.newRepo = DEFAULT_REGISTRY_REPO;
    this.newRepoOther = '';
    this._tickerColumnManuallyPicked = false;
  }

  /**
   * `select`-mode: caller selected a known repo from the dropdown
   * (newRepo becomes the effective slug directly).
   * `other`-mode: caller picked the "Other…" sentinel — reveal the
   * text input; effective slug reads from newRepoOther.
   */
  get repoInputMode(): 'select' | 'other' {
    return this.newRepo === this.REPO_OTHER ? 'other' : 'select';
  }

  onRepoSelectChange(v: string) {
    this.newRepo = v;
    this.editorDirty = true;
    if (v !== this.REPO_OTHER) {
      // Leaving 'other' mode — clear the text field so a stale
      // half-typed slug doesn't bleed into a subsequent 'other' toggle.
      this.newRepoOther = '';
    }
  }

  onRepoOtherChange(v: string) {
    this.newRepoOther = String(v || '').trim();
    this.editorDirty = true;
  }

  /**
   * Effective repo slug for the save-side registry.create call. Reads
   * from newRepo when a dropdown pick is active, from newRepoOther
   * when 'Other…' is selected. Callers should validate before use;
   * this getter is a plain accessor, not a validator.
   */
  get effectiveRepo(): string {
    return this.repoInputMode === 'other'
      ? this.newRepoOther.trim()
      : (this.newRepo || '').trim();
  }

  onContentChange() {
    this.editorDirty = true;
    // Re-run the CSV ticker-column auto-pick when content changes — the
    // header row may have just landed via paste or file upload. Skip if
    // the operator already picked a column manually so we don't clobber.
    if (this.mode === 'new' && this.newFormat === 'csv' && !this._tickerColumnManuallyPicked) {
      this._autoPickTickerColumn();
    }
  }

  // Sanitize the free-text new-list name to the shape the backend
  // accepts: lowercase, alphanumeric + hyphen + underscore, must start
  // with a letter or digit, ≤64 chars. Applied on every keystroke so
  // the user can't type into an invalid state — matches the save-time
  // regex /^[a-z0-9][a-z0-9_-]{0,63}$/ (ER-DVV4RVS 2026-08-14: user
  // typed a name with a space / uppercase / punctuation, save toast
  // fired, opaque "Invalid name" error report landed on the tracker).
  onNewNameChange() {
    this.editorDirty = true;
    const raw = String(this.newName ?? '');
    const sanitized = raw
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')     // any other char → hyphen
      .replace(/^[^a-z0-9]+/, '')         // strip leading non-alphanumeric
      .replace(/-{2,}/g, '-')             // collapse runs of hyphens
      .replace(/-$/, '')                  // strip trailing hyphen
      .slice(0, 64);
    if (sanitized !== raw) this.newName = sanitized;
  }

  onFormatChange(fmt: 'txt' | 'csv') {
    if (this.newFormat === fmt) return;
    this.newFormat = fmt;
    this.editorDirty = true;
    if (fmt === 'csv') {
      // Try auto-picking a ticker column right away — the operator may
      // have already pasted a CSV before flipping the toggle.
      if (!this._tickerColumnManuallyPicked) this._autoPickTickerColumn();
    } else {
      // Reset ticker-column state so a later flip back to csv triggers
      // a fresh auto-pick against whatever content is present then.
      this.newTickerColumn = '';
      this._tickerColumnManuallyPicked = false;
    }
  }

  onTickerColumnChange(col: string) {
    this.newTickerColumn = col;
    this._tickerColumnManuallyPicked = true;
    this.editorDirty = true;
  }

  /**
   * Distinct set of repos in use by currently-registered lists.
   * Feeds the New-list repo <select> so the operator picks one they've
   * already been committing to. Always includes DEFAULT_REGISTRY_REPO
   * so single-repo shops see the shared repo on first-run empty state.
   */
  get knownRepos(): string[] {
    const set = new Set<string>([DEFAULT_REGISTRY_REPO]);
    for (const l of this.lists) {
      const r = l.manifest?.source_repo;
      if (r && typeof r === 'string') set.add(r);
    }
    return Array.from(set).sort();
  }

  /**
   * Trigger the hidden <input type=file> — click handler on the
   * `Upload file…` button. Kept as a method so the template stays clean
   * (nativeElement access in template gets flagged by Angular's strict
   * template checker).
   */
  triggerFileInput() {
    if (this.editorDirty && this.mode === 'new' && (this.newName || this.editorContent) &&
        !confirm('Replace current draft with the uploaded file?')) return;
    this.fileInput?.nativeElement?.click();
  }

  /**
   * File-input change handler. Auto-fills name from filename (stripped
   * of extension, lowercase-sanitized), type from extension, and
   * content from bytes. For CSV, the auto-picker fires on content set.
   */
  async onFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    // Clear the input value so re-uploading the same file (after a bad
    // save, for example) still fires (change) — browsers suppress the
    // event when value is unchanged.
    input.value = '';
    const text = await file.text();
    // Filename → list name: strip extension, lowercase, replace any run
    // of non-[a-z0-9_-] with '_'. Matches the save-side validator.
    const rawStem = file.name.replace(/\.(txt|csv)$/i, '');
    const sanitized = rawStem
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const isCsv = /\.csv$/i.test(file.name);
    // Ensure new-list mode — the upload button is only visible there
    // anyway, but a defensive setup keeps this callable from anywhere.
    if (this.mode !== 'new') this.startNew();
    this.newName = sanitized;
    this.newFormat = isCsv ? 'csv' : 'txt';
    this.editorContent = text;
    this.editorDirty = true;
    this._tickerColumnManuallyPicked = false;
    if (this.newFormat === 'csv') this._autoPickTickerColumn();
  }

  /**
   * Parse the first non-comment, non-blank line of `editorContent` as
   * a CSV header row. Returns an empty array when the content doesn't
   * parse or there are no headers.
   *
   * We intentionally use a naive split-on-comma parser — the shipped
   * files are simple `Ticker,Name,Exchange`-style headers; if a real
   * quoted-comma-inside-a-field header ever shows up we can swap in
   * PapaParse. Right now: not worth 15KB of bundle for the header row.
   */
  get csvHeaders(): string[] {
    if (this.newFormat !== 'csv') return [];
    for (const line of this.editorContent.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith('#')) continue;
      return t.split(',').map(h => h.trim()).filter(h => h.length > 0);
    }
    return [];
  }

  private _autoPickTickerColumn() {
    const headers = this.csvHeaders;
    if (headers.length === 0) return;
    // Look for a header that names itself as the ticker column. Case-
    // insensitive; matches common conventions. Fall back to the first
    // column so the operator has SOMETHING selected when the row is
    // atypical (e.g. cusip-first files).
    const found = headers.find(h => /^(symbol|ticker|sym)$/i.test(h));
    this.newTickerColumn = found || headers[0];
  }

  cancelEdit() {
    if (this.editorDirty && !confirm('Discard unsaved changes?')) return;
    this.mode = 'idle';
    this.selectedName = '';
    this.newName = '';
    this.editorContent = '';
    this.editorSha = '';
    this.editorDirty = false;
  }

  async save() {
    const name = (this.mode === 'new' ? this.newName : this.selectedName).trim().toLowerCase();
    if (!name) {
      this.toast.error('Name required', 'Pick a list name (lowercase, alphanumeric, hyphen/underscore).');
      return;
    }
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(name)) {
      this.toast.error('Invalid name', 'Name must start with a letter or digit and contain only lowercase letters, digits, hyphens, or underscores.');
      return;
    }
    if (!this.editorContent.trim()) {
      this.toast.error('Empty file', 'Add at least one symbol before saving.');
      return;
    }
    // Determine the effective format for the commit path. For new lists
    // the operator picks; for existing lists we honor the on-disk
    // format tracked in viewFormat (populated on selectList).
    const format: 'txt' | 'csv' = this.mode === 'new' ? this.newFormat : this.viewFormat;
    // CSV requires a ticker column so the parser knows which field to
    // pull symbols from. Enforce here so a bad create doesn't reach
    // the backend and land as a broken registry doc.
    if (this.mode === 'new' && format === 'csv') {
      if (this.csvHeaders.length === 0) {
        this.toast.error('CSV parse failed', 'Could not read a header row from the file — first non-comment line should be comma-separated column names.');
        return;
      }
      if (!this.newTickerColumn) {
        this.toast.error('Ticker column required', 'Pick which CSV column holds the ticker symbol.');
        return;
      }
      if (!this.csvHeaders.includes(this.newTickerColumn)) {
        this.toast.error('Ticker column not in header', `Column "${this.newTickerColumn}" isn't in the CSV header row. Re-pick from the dropdown.`);
        return;
      }
    }
    this.saving = true;
    try {
      // NEW-list preflight: create the registry doc first. Backend's
      // admin_upsert_file requires a matching registry entry in mongo
      // mode (throws if missing). The registry doc carries format +
      // ticker_column — the file bytes alone don't tell the parser
      // whether to split on lines or on the ticker-column value.
      if (this.mode === 'new') {
        const repo = this.effectiveRepo || DEFAULT_REGISTRY_REPO;
        // Repo slug validation — backend enforces owner/name split but
        // it's a friendlier error surface to catch here. Allow the full
        // GitHub-permitted char set for owner + name.
        if (!/^[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._-]*$/.test(repo)) {
          const msg = this.repoInputMode === 'other'
            ? 'Type a repo slug like `owner/name`, e.g. `F2Tech-WebDev-v2/theo-trade-symbol-lists`.'
            : 'Repo must look like `owner/name`. Pick one from the dropdown, or choose Other… to type a new one.';
          this.toast.error('Invalid repo', msg);
          return;
        }
        const path = `lists/${name}.${format}`;
        const regRes = await this.registry.create({
          name,
          repo,
          branch: DEFAULT_REGISTRY_BRANCH,
          path,
          format,
          ticker_column: format === 'csv' ? this.newTickerColumn : undefined,
          active: true,
        });
        if (!regRes?.ok) return;  // toast already fired from the service
      }
      const res = await this.svc.upsertFile(name, this.editorContent);
      if (!res?.ok) return;
      this.toast.success(res.action === 'created' ? 'Created' : 'Saved',
        `${name}.${format} @ ${res.commit_sha.slice(0, 7)}`);
      this.mode = 'view';
      this.selectedName = res.name;
      this.newName = '';
      this.editorDirty = false;
      this.viewFormat = format;
      const f = await this.svc.getFile(res.name);
      if (f) {
        this.editorSha = f.sha;
        this.editorContent = f.content;
      }
      await this.reload();
    } finally {
      this.saving = false;
    }
  }

  async remove() {
    if (this.mode !== 'view' || !this.selectedName) return;
    if (!confirm(`Delete list "${this.selectedName}"? This commits a delete to the repo and drops the Redis cache entry.`)) return;
    this.deleting = true;
    try {
      const res = await this.svc.deleteFile(this.selectedName);
      if (!res?.ok) return;
      this.toast.success('Deleted', `${this.selectedName}.${this.viewFormat} @ ${res.commit_sha.slice(0, 7)}`);
      this.mode = 'idle';
      this.selectedName = '';
      this.editorContent = '';
      this.editorSha = '';
      this.editorDirty = false;
      await this.reload();
    } finally {
      this.deleting = false;
    }
  }

  async manualRefresh() {
    const res = await this.svc.refresh();
    if (res?.ok) {
      this.toast.success('Refreshed', `${res.count_lists} lists, ${res.count_symbols} symbols`);
      await this.reload();
    }
  }

  get parsedCount(): number {
    // Effective format for parse: new-list uses the toggle, view mode
    // uses the file's on-disk format.
    const fmt = this.mode === 'new' ? this.newFormat : this.viewFormat;
    if (fmt === 'csv') return this._parsedCountCsv();
    return this._parsedCountTxt();
  }

  private _parsedCountTxt(): number {
    let n = 0;
    const seen = new Set<string>();
    for (const line of this.editorContent.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith('#')) continue;
      const s = t.toUpperCase();
      if (!/^[A-Z][A-Z0-9.\-]{0,6}$/.test(s)) continue;
      if (seen.has(s)) continue;
      seen.add(s);
      n++;
    }
    return n;
  }

  private _parsedCountCsv(): number {
    // Count valid tickers in the ticker column. We only run this on
    // new-list where newTickerColumn is known; view-mode csv still
    // counts data rows as a decent proxy.
    const lines = this.editorContent.split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l && !l.startsWith('#'));
    if (lines.length <= 1) return 0;
    const headers = lines[0].split(',').map(h => h.trim());
    let tickerIdx = -1;
    if (this.mode === 'new' && this.newTickerColumn) {
      tickerIdx = headers.indexOf(this.newTickerColumn);
    }
    if (tickerIdx < 0) return Math.max(0, lines.length - 1);
    const seen = new Set<string>();
    let n = 0;
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map(c => c.trim());
      const raw = (cols[tickerIdx] || '').toUpperCase();
      if (!/^[A-Z][A-Z0-9.\-]{0,6}$/.test(raw)) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      n++;
    }
    return n;
  }

  trackByName(_: number, l: SymbolListSummary) { return l.name; }
}
